import _ from 'lodash';
import { axiosAPI as axios, withLoading } from 'utils';
import {
  FAMILY_UPDATE,
  SHOW_HELP,
  SHOW_SETTINGS,
  CLOSE_SETTINGS,
  SET_MENU_BUTTONS,
  SET_PREV_MENU_BUTTONS,
  SHOW_INITIAL_SETTINGS,
  SET_FLINK_PLAY_DATA,
  SET_CURRENT_MENU,
  SET_TEMPLATE_DATA,
  SET_SOLUTION,
  SHOW_VIDEO,
  SET_ACTIVITY_GROUP,
  SAVE_MAIN_MENU_SCROLL_POSITION,
  UPDATE_LEARNER,
  UPDATE_TEAM,
  GO_BACK_ACTION,
  SHOW_FAMILY_REPORTS,
  RESET_FLINK_PLAY,
  SET_ACTIVITY_PROGRESS,
  CLOSE_ACTIVITY,
  SET_ACTIVITY_TO_PLAY,
  SHOW_PARENT_ADVICE,
  SHOW_QUICKSTART_VIDEOS,
  SET_PARENT_ADVICE_URL,
  SET_PARENT_ADVICE_COORDS,
  SET_FEEDBACK_COORDS,
  OPEN_WRITING_FEEDBACK,
  CLOSE_WRITING_FEEDBACK,
  SHOW_FAMILY_POINTS_HISTORY,
  CLOSE_FAMILY_POINTS_HISTORY,
  SET_FAMILY_POINTS,
  SET_PREVIOUS_WEEK_TIME,
} from 'actions/types';
import {
  getTranslateFunc,
  calculateReports,
  removeParenthesis,
  isMobileAppRuntime,
} from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import {
  applyMobileInstructionLanguageAddon,
  appendPlacementLocaleDiagnostic,
  normalizePlacementLanguageCode,
} from 'mobile-modules/locale/placement-language-addon';
//import { audioURL, filesURL } from 'config';
import { audioURL, filesURL, s3bucketPublicURL } from 'config';
import getLocaleAudioFolderName from 'utils/getLocaleAudioFolderName';
import { CONCENTRATION } from 'consts/activity-templates';
import { GO_BACK } from 'consts/buttons';
import { logout } from 'actions/authActions';
import { ensureWebPlayLocaleStrings } from 'actions/commonActions';
import api from 'api';
import {
  DESKTOP_ASSESSMENT_MODES,
  findFreeTrialActivityGroup,
  getAssessmentGroupValidation,
  getAssessmentMappingForGroup,
  getAssessmentNestedGroupIds,
  getAssessmentLeafSlots,
  getAssessmentActivityKeyFromLabel,
  DEFAULT_AUTHORED_ACTIVITY_MAPPING,
  getFlattenedExpectedActivityPlan,
  getAuthoredAssessmentIndex,
  buildAssessmentSubmissions,
} from 'utils/desktopAssessment';
import {
  cachePlayBootstrap,
  getCachedPlayBootstrap,
} from 'mobile-modules/offline/cache';
import { prepareAssessmentCache } from 'mobile-modules/offline/assessment';
import { isOnline } from 'mobile-modules/offline/state';
import { enqueueRequest } from 'mobile-modules/offline/queue';
import {
  attachActiveMobileFamilyFriend,
  isMobileLocalFriend,
  updateMobileFamilyFriend,
} from 'utils/mobileFamilyFriends';

const filter = 'flinkPlay';

const mergeLearnerPatch = (learner, learnerId, data = {}) => {
  if (!learner) return learner;
  const id = String(learner?._id || learner?.id || '');
  if (!id || id !== String(learnerId || '')) return learner;
  return { ...learner, ...data };
};

const persistOfflineLearnerUpdates = async (updates = []) => {
  if (!isMobileAppRuntime() || !Array.isArray(updates) || !updates.length) return;

  const normalized = updates
    .map((item) => ({
      learnerId: String(item?.learnerId || ''),
      data: item?.data && typeof item.data === 'object' ? item.data : {},
    }))
    .filter((item) => item.learnerId);

  if (!normalized.length) return;

  const cached = await getCachedPlayBootstrap();
  if (!cached) return;

  const byId = new Map(normalized.map((item) => [item.learnerId, item.data]));
  const patchLearner = (learner) => {
    const id = String(learner?._id || learner?.id || '');
    const data = id ? byId.get(id) : null;
    return data ? { ...learner, ...data } : learner;
  };

  const next = {
    ...cached,
    learner: patchLearner(cached.learner),
    ...(Array.isArray(cached.team) ? { team: cached.team.map(patchLearner) } : {}),
  };

  await cachePlayBootstrap(next);
};

const normalizeSolutionLanguageMode = (solution = {}) => {
  const interfaceLocaleCode = String(solution.locale || 'en').trim() || 'en';
  const requestedMode = String(solution.languageMode || 'immersion').trim().toLowerCase();
  const languageMode = interfaceLocaleCode === 'en'
    ? 'immersion'
    : (requestedMode === 'transition' ? 'transition' : 'immersion');
  const activityLocaleCode = languageMode === 'transition'
    ? (String(solution.activityLocale || 'en').trim() || 'en')
    : interfaceLocaleCode;

  return {
    interfaceLocaleCode,
    activityLocaleCode,
    languageMode,
  };
};

const findLocaleOrFallback = (locales = [], code = 'en', fallbackCode = 'en') => (
  _.find(locales, { code }) ||
  _.find(locales, { code: fallbackCode }) ||
  _.first(locales) ||
  { code: fallbackCode, name: fallbackCode, displayName: fallbackCode }
);


const toIdString = (value) => (value === null || value === undefined ? null : String(value));

const getActivityById = (solutionMenu = {}, activityId) => {
  const activities = Array.isArray(solutionMenu?.activities) ? solutionMenu.activities : [];
  return activities.find((item) => toIdString(item?._id) === toIdString(activityId)) || null;
};

const getActivityLabel = (activity = {}) => [
  activity?.activityName,
  activity?.formattedName,
  activity?.name,
  activity?.title,
]
  .filter(Boolean)
  .join(' ');

const getNumericValue = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const shouldShowMenuHelpAutomatically = (menu, learner) => {
  const limit = getNumericValue(menu?.helpDisplayAutomatically, 0);
  if (!limit || limit < 1 || !learner) return false;

  // Mobile app only: show automatic menu help once per menu.
  // Desktop/web keeps the existing helpDisplayAutomatically/login-count behavior unchanged.
  if (isMobileAppRuntime()) {
    const menuId = toIdString(menu?._id);
    const menuAlreadyShown = Boolean(
      menuId && (learner.menusShown || []).some((shownMenuId) => toIdString(shownMenuId) === menuId)
    );
    if (menuAlreadyShown) return false;
  }

  const totalLogins = getNumericValue(
    learner.loginsCount,
    getNumericValue(learner.singleLoginsCount, 0) + getNumericValue(learner.togetherLoginsCount, 0)
  );

  return totalLogins > 0 && totalLogins <= limit;
};

const isAssessmentAttemptForCurrentMember = (attempt, learnerId, solutionId) => (
  attempt &&
  toIdString(attempt.familyMemberId) === toIdString(learnerId) &&
  toIdString(attempt.solutionId) === toIdString(solutionId)
);

const unwrapMobileAssessmentAttempt = (response) => {
  if (!response) return null;

  return (
    response.attempt ||
    response.data?.attempt ||
    response.data?.data?.attempt ||
    response.data?.data ||
    response.data ||
    response
  );
};

const toFiniteNumberOrNull = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const buildAssessmentStatsFromGameFeedback = (game = {}) => {
  const feedbackStats = game?.assessmentFeedbackStats || null;
  const correctAnswers = toFiniteNumberOrNull(feedbackStats?.correctAnswers);
  const totalQuestions = toFiniteNumberOrNull(feedbackStats?.totalQuestions);

  if (correctAnswers === null || totalQuestions === null || totalQuestions <= 0) {
    return null;
  }

  return {
    correctAnswers,
    problemsAnsweredCorrectly: correctAnswers,
    totalQuestions,
    allProblemsCount: totalQuestions,
    problemsAnsweredIncorrectly: Math.max(0, totalQuestions - correctAnswers),
    isMastered: totalQuestions > 0 && correctAnswers >= totalQuestions,
    rawAssessment: {
      correctAnswers,
      totalQuestions,
      source: 'game-assessment-feedback-stats',
    },
  };
};

const submitDesktopAssessmentResultFromCloseActivity = async ({ getState, learnerId, stateSnapshot = null }) => {
  const state = stateSnapshot || getState();
  const {
    flinkPlay = {},
    game = {},
  } = state;

  const {
    activity: activityId,
    activityProgress = {},
    currentActivityGroup,
    solutionMenu,
    solutionLocale,
    activityLocale,
    solution,
    learner,
  } = flinkPlay;

  const baseLog = {
    learnerId,
    currentLearnerId: learner?._id || null,
    solutionId: solution?._id || null,
    activityId,
    currentActivityGroup,
  };

  if (!activityId || !learner?._id || toIdString(learner._id) !== toIdString(learnerId)) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'not_current_learner_or_missing_activity',
    });
    return null;
  }

  if (!solution?._id || !solutionMenu) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'missing_solution_or_menu',
    });
    return null;
  }

  const assessmentRootGroup = findFreeTrialActivityGroup(solutionMenu);
  const validation = getAssessmentGroupValidation(solutionMenu, assessmentRootGroup);

  if (!assessmentRootGroup?._id || !validation?.isValid) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'assessment_group_not_available',
      validationMessage: validation?.message || null,
    });
    return null;
  }

  const assessmentGroupIds = getAssessmentNestedGroupIds(solutionMenu, assessmentRootGroup).map(String);
  const assessmentLeafSlots = getAssessmentLeafSlots(solutionMenu, assessmentRootGroup);
  const assessmentLeafActivityIds = assessmentLeafSlots.map((slot) => toIdString(slot.activityId)).filter(Boolean);
  const inAssessmentGroup = assessmentGroupIds.includes(toIdString(currentActivityGroup));
  const inAssessmentLeafActivities = assessmentLeafActivityIds.includes(toIdString(activityId));

  if (!inAssessmentGroup && !inAssessmentLeafActivities) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'not_assessment_activity',
      assessmentGroupIds,
      assessmentLeafActivityIds,
    });
    return null;
  }

  const activityDoc = getActivityById(solutionMenu, activityId) || { _id: activityId };
  const feedbackAssessmentStats = buildAssessmentStatsFromGameFeedback(game);
  const effectiveAssessmentStats =
    activityProgress?.assessmentStats ||
    game.stats?.data?.rawAssessment ||
    game.stats?.data ||
    feedbackAssessmentStats ||
    null;
  const effectiveActivityProgress = {
    ...(activityProgress || {}),
    ...(effectiveAssessmentStats ? { assessmentStats: effectiveAssessmentStats } : {}),
  };
  const hasCompletedActivitySignal = Boolean(
    activityProgress?.mastered ||
    activityProgress?.partial ||
    activityProgress?.assessmentStats ||
    game.stats
  );

  if (!hasCompletedActivitySignal) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'activity_not_completed',
      activityProgress,
      feedbackAssessmentStats,
    });
    return null;
  }

  const currentActivityForAssessment = {
    activity: {
      ...activityDoc,
      rawAssessment: effectiveAssessmentStats?.rawAssessment || effectiveAssessmentStats || activityDoc?.rawAssessment || null,
    },
    data: {
      rawAssessment: effectiveAssessmentStats?.rawAssessment || effectiveAssessmentStats || null,
    },
  };
  const activityLabel = getActivityLabel(activityDoc);
  const explicitActivityKey = getAssessmentActivityKeyFromLabel(activityLabel);
  const authoredIndex = explicitActivityKey ? null : getAuthoredAssessmentIndex({
    solutionMenu,
    assessmentRootGroupId: assessmentRootGroup._id,
    currentActivityGroup,
    activityId,
    currentActivity: currentActivityForAssessment,
  });
  const mapping = getAssessmentMappingForGroup(assessmentRootGroup, solutionMenu);
  const logicalKeys = explicitActivityKey
    ? [explicitActivityKey]
    : (mapping?.[authoredIndex] || []);

  console.info('[desktop-assessment][action-submit-map]', {
    ...baseLog,
    assessmentRootGroupId: assessmentRootGroup._id,
    activityLabel,
    explicitActivityKey,
    authoredIndex,
    logicalKeys,
    activityProgress,
    effectiveActivityProgress,
    feedbackAssessmentStats,
  });

  if (!logicalKeys.length) {
    console.warn('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'mapping_not_resolved',
      activityLabel,
      authoredIndex,
    });
    return null;
  }

  const mode = isMobileAppRuntime()
    ? DESKTOP_ASSESSMENT_MODES.MOBILE
    : DESKTOP_ASSESSMENT_MODES.ONLINE;
  let attempt = unwrapMobileAssessmentAttempt(await api.mobileAssessment.getCurrentAttempt({
    familyMemberId: learner._id,
    solutionId: solution._id,
    mode,
  }));

  if (!isAssessmentAttemptForCurrentMember(attempt, learner._id, solution._id)) {
    console.info('[desktop-assessment][action-submit-ensure-attempt]', {
      ...baseLog,
      reason: 'current_attempt_not_found_or_wrong_member',
      previousAttemptId: attempt?._id || null,
      previousAttemptFamilyMemberId: attempt?.familyMemberId || null,
      previousAttemptSolutionId: attempt?.solutionId || null,
      assessmentRootGroupId: assessmentRootGroup._id,
    });

    const startPayload = {
      familyMemberId: learner._id,
      explanationLanguage: solutionLocale?.code || null,
      instructionLanguage: activityLocale?.code || solutionLocale?.code || null,
      language: activityLocale?.code || solutionLocale?.code || null,
      assessmentVersion: 'online-desktop-v11',
      activityPlan: getFlattenedExpectedActivityPlan(mapping || DEFAULT_AUTHORED_ACTIVITY_MAPPING),
      assessmentRootGroupId: assessmentRootGroup._id,
      mode,
      solutionId: solution._id,
      forceNewAttempt: false,
    };

    console.info('[desktop-assessment][action-submit-start-attempt]', {
      ...baseLog,
      startPayload,
    });

    const startResponse = await api.mobileAssessment.start(startPayload);
    attempt = unwrapMobileAssessmentAttempt(startResponse);

    console.info('[desktop-assessment][action-submit-start-attempt-success]', {
      ...baseLog,
      attemptId: attempt?._id || null,
      attemptFamilyMemberId: attempt?.familyMemberId || null,
      attemptSolutionId: attempt?.solutionId || null,
    });
  }

  if (!isAssessmentAttemptForCurrentMember(attempt, learner._id, solution._id)) {
    console.warn('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'attempt_still_missing_after_start',
      attemptId: attempt?._id || null,
      attemptFamilyMemberId: attempt?.familyMemberId || null,
      attemptSolutionId: attempt?.solutionId || null,
    });
    return null;
  }

  const existingKeys = new Set((attempt.activityResults || []).map((item) => item?.activityKey).filter(Boolean));
  const isMastered = Boolean(
    effectiveActivityProgress?.mastered ||
    effectiveAssessmentStats?.isMastered ||
    (
      Number(effectiveAssessmentStats?.totalQuestions || effectiveAssessmentStats?.allProblemsCount || 0) > 0 &&
      Number(effectiveAssessmentStats?.correctAnswers || effectiveAssessmentStats?.problemsAnsweredCorrectly || 0) >=
        Number(effectiveAssessmentStats?.totalQuestions || effectiveAssessmentStats?.allProblemsCount || 0)
    )
  );
  const submissions = buildAssessmentSubmissions({
    logicalKeys,
    stats: game.stats || {},
    activityProgress: effectiveActivityProgress || {},
    currentActivity: currentActivityForAssessment,
    isMastered,
  }).filter((submission) => !existingKeys.has(submission.activityKey));

  console.info('[desktop-assessment][action-submit-score]', {
    ...baseLog,
    activityProgress,
    effectiveActivityProgress,
    feedbackAssessmentStats,
    gameStatsData: game.stats?.data || null,
    submissions: submissions.map((submission) => ({
      activityKey: submission.activityKey,
      correctAnswers: submission.payload?.correctAnswers,
      totalQuestions: submission.payload?.totalQuestions,
      star: submission.payload?.star || null,
      isGreen: submission.payload?.isGreen || null,
    })),
  });

  if (!submissions.length) {
    console.info('[desktop-assessment][action-submit-skip]', {
      ...baseLog,
      reason: 'already_recorded',
      attemptId: attempt._id,
      logicalKeys,
      existingKeys: Array.from(existingKeys),
    });
    return attempt;
  }

  let latestAttempt = attempt;

  for (const submission of submissions) {
    const requestBody = {
      activityKey: submission.activityKey,
      payload: {
        ...submission.payload,
        sourceActivityId: activityId || null,
        sourceActivityGroupId: currentActivityGroup || null,
      },
      sourceActivityId: activityId || null,
      sourceActivityGroupId: currentActivityGroup || null,
      allowReplay: true,
    };

    console.info('[desktop-assessment][action-submit-request]', {
      ...baseLog,
      attemptId: latestAttempt?._id || attempt._id,
      requestBody,
    });

    const response = await api.mobileAssessment.submitActivity(latestAttempt?._id || attempt._id, requestBody);
    latestAttempt = unwrapMobileAssessmentAttempt(response) || latestAttempt;

    console.info('[desktop-assessment][action-submit-success]', {
      ...baseLog,
      attemptId: latestAttempt?._id || attempt._id,
      activityKey: submission.activityKey,
      resultCount: latestAttempt?.activityResults?.length || 0,
    });
  }

  return latestAttempt;
};


export const getFlinkPlayData = () => (dispatch, getState) =>
  withLoading(dispatch, async () => {
    // Native iOS/Android keeps the existing online endpoint as the source of truth,
    // but stores the last successful bootstrap for transparent offline fallback.
    const mobileRuntime = isMobileAppRuntime();
    const endpoint = mobileRuntime
      ? '/api/mobile-v2/play/bootstrap'
      : '/api/flink-play/get-data';

    if (mobileRuntime && !isOnline()) {
      const cached = await getCachedPlayBootstrap();
      if (!cached) {
        return Promise.reject(new Error('Learning session is not available offline on this device yet.'));
      }
      const playData = attachActiveMobileFamilyFriend(cached);
      dispatch({ filter, type: SET_FLINK_PLAY_DATA, payload: playData });
      console.info('[mobile-offline] using cached Play bootstrap');
      return playData;
    }

    if (mobileRuntime && isOnline()) {
      const cached = await getCachedPlayBootstrap();
      const authLearner = getState()?.auth?.learner;
      const authLearnerId = String(
        (authLearner && typeof authLearner === 'object' ? (authLearner._id || authLearner.id) : authLearner) || '',
      );
      const cachedLearnerId = String(cached?.learner?._id || cached?.learner?.id || '');
      if (cached && authLearnerId && cachedLearnerId && authLearnerId === cachedLearnerId) {
        const playData = attachActiveMobileFamilyFriend(cached);
        dispatch({ filter, type: SET_FLINK_PLAY_DATA, payload: playData });
        console.info('[mobile-play-bootstrap] using matching cached bootstrap; refreshing in background');
        axios.get(endpoint, { timeout: 45000 })
          .then(async (response) => {
            const fresh = attachActiveMobileFamilyFriend(response.data);
            await cachePlayBootstrap(fresh);
            prepareAssessmentCache(api.mobileAssessment, fresh).catch(() => undefined);
          })
          .catch((error) => console.info('[mobile-play-bootstrap] background refresh skipped', error?.message || error));
        return playData;
      }
    }

    try {
      const response = await axios.get(endpoint, { timeout: 45000 });
      const playData = attachActiveMobileFamilyFriend(response.data);
      if (mobileRuntime) {
        await cachePlayBootstrap(playData);
        prepareAssessmentCache(api.mobileAssessment, playData).catch(() => undefined);
      }
      dispatch({ filter, type: SET_FLINK_PLAY_DATA, payload: playData });
      return playData;
    } catch (err) {
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''))) {
        console.error('[mobile-play-bootstrap][timeout]', {
          endpoint,
          message: err?.message || String(err),
        });
      }
      if (mobileRuntime) {
        const cached = await getCachedPlayBootstrap();
        if (cached) {
          const playData = attachActiveMobileFamilyFriend(cached);
          dispatch({ filter, type: SET_FLINK_PLAY_DATA, payload: playData });
          console.info('[mobile-offline] using cached Play bootstrap');
          return playData;
        }
      }
      return Promise.reject(err.response?.data || err);
    }
  });

export const showHelp = (payload) => ({
  filter,
  type: SHOW_HELP,
  payload,
});

const findStringByNumber = (strings = [], stringNumber) => _.find(
  strings,
  (item) => Number(item?.stringNumber) === Number(stringNumber)
);

const resolveStringAudio = (string, requestedLocaleCode = 'en') => {
  const normalizedCode = String(requestedLocaleCode || 'en').trim() || 'en';
  const baseCode = normalizedCode.split('-')[0];
  const localeCodes = [...new Set([normalizedCode, baseCode, 'en'].filter(Boolean))];

  for (const localeCode of localeCodes) {
    const localizedString = _.get(string, ['data', localeCode]);
    const filename = localizedString?.stringAudio || localizedString?.audio;

    if (filename) {
      return {
        filename: String(filename).trim(),
        localeCode,
      };
    }
  }

  return { filename: '', localeCode: normalizedCode };
};

const buildStringAudioUrl = (filename, locale) => {
  if (!filename) return '';
  if (/^(?:https?:|blob:|data:)/i.test(filename)) return filename;

  const localeFolder = getLocaleAudioFolderName(locale);
  return `${audioURL}/Generic/Strings/${localeFolder}/${filename}`;
};

export const showSettingsHelpDialog = (payload = {}) => (dispatch, getState) => {
  const {
    common: { strings = [], locales = [] } = {},
    flinkPlay: { solutionLocale } = {},
    status: { lang } = {},
  } = getState();

  const locale = solutionLocale || lang || {};
  const localeCode = locale?.code || 'en';
  const localeName = locale?.name || 'English';
  const stringNumber = Number(payload?.stringNumber);

  const string = findStringByNumber(strings, stringNumber);
  const resolvedAudio = resolveStringAudio(string, localeCode);
  const audioLocale = _.find(locales, { code: resolvedAudio.localeCode })
    || (resolvedAudio.localeCode === localeCode ? locale : null)
    || { code: 'en', name: 'English' };
  const audioHelp = buildStringAudioUrl(resolvedAudio.filename, audioLocale) || null;

  console.log('[showSettingsHelpDialog]', {
    stringNumber,
    localeCode,
    localeName,
    resolvedAudioLocale: resolvedAudio.localeCode,
    stringAudio: resolvedAudio.filename || null,
    audioHelp,
  });

  dispatch(
    showHelp({
      ...payload,
      stringNumber,
      audioHelp,
      audioDebugContext: `Settings Help | String #${stringNumber} | Locale ${localeCode}`,
    })
  );
};

export const showSettings = () => ({
  filter,
  type: SHOW_SETTINGS,
});

export const showInitialSettings = () => ({
  filter,
  type: SHOW_INITIAL_SETTINGS,
});

export const setParentAdviceUrl = (payload) => ({
  filter,
  type: SET_PARENT_ADVICE_URL,
  payload,
});

export const setActivityPartial = (additionalData = {}) => ({
  filter,
  type: SET_ACTIVITY_PROGRESS,
  payload: { partial: true, ...additionalData },
});

export const setActivityMastered = (additionalData = {}) => ({
  filter,
  type: SET_ACTIVITY_PROGRESS,
  payload: { mastered: true, partial: true, ...additionalData },
});

export const setActivityAssessmentStats = (assessmentStats = {}) => ({
  filter,
  type: SET_ACTIVITY_PROGRESS,
  payload: { assessmentStats },
});

export const setFamilyPoints = (points) => ({
  filter,
  type: SET_FAMILY_POINTS,
  payload: points,
});

export const closeActivity = (options = {}) => async (dispatch, getState) => {
  const { immediate = false } = options || {};
  const stateSnapshot = getState();
  const {
    flinkPlay,
    game,
    common: { activityTemplates: templates },
  } = stateSnapshot;

  if (game.isPreview) return;

  const {
    activity,
    activityProgress,
    learner,
    team,
    groupsBreadcrumbs,
    currentActivityGroup,
    solutionMenu,
    learningCenter,
  } = flinkPlay;

  let didCloseActivity = false;
  const out = () => {
    if (didCloseActivity) return;
    didCloseActivity = true;

    dispatch({
      filter,
      type: CLOSE_ACTIVITY,
    });

    if (
      !groupsBreadcrumbs.length &&
      !currentActivityGroup &&
      solutionMenu.menuGroups &&
      solutionMenu.menuGroups.length === 1
    ) {
      dispatch(logout());
    }
  };

  // Android placement Results must leave the modal immediately; persistence continues
  // against the captured pre-close state. Default callers retain the original timing.
  if (immediate) out();

  const updateLearnerReports = (member) => {
    const reports = { ...(member.reports || {}) };

    reports[activity] = {
      ...(reports[activity] || {}),
      ...(activityProgress || {}),
    };

    const newReports = calculateReports({
      templates,
      solutionMenu,
      reports,
      learningCenterConfig: learningCenter?.config || {},
      isTeam: !!(team && team.length),
    });

    return { learnerId: member._id, data: { reports: newReports } };
  };

  const addTimeInApp = game.gameStartedAt
    ? (Date.now() - game.gameStartedAt - game.idleTime)
    : null;

  const addLocalTime = (member, isTeamTime) => {
    if (!Number.isFinite(addTimeInApp) || addTimeInApp <= 0 || addTimeInApp >= 24 * 60 * 60 * 1000) {
      return Array.isArray(member.timeInApp) ? member.timeInApp : [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedSolutionId = member?.settings?.selectedSolutionId || null;
    const timeInApp = (Array.isArray(member.timeInApp) ? member.timeInApp : []).map(item => ({
      ...item,
      byModule: { ...(item?.byModule || {}) },
    }));
    const existingIndex = timeInApp.findIndex(item => {
      const date = item?.date ? new Date(item.date) : null;
      return date && !Number.isNaN(date.getTime()) && date.setHours(0, 0, 0, 0) === today.getTime();
    });
    const current = existingIndex >= 0
      ? timeInApp[existingIndex]
      : { date: today.toISOString(), ms: 0, togetherMs: 0, byModule: {} };

    current.ms = Number(current.ms || 0) + (isTeamTime ? 0 : addTimeInApp);
    current.togetherMs = Number(current.togetherMs || 0) + (isTeamTime ? addTimeInApp : 0);
    if (selectedSolutionId) {
      const moduleTime = current.byModule?.[selectedSolutionId] || {};
      current.byModule = {
        ...(current.byModule || {}),
        [selectedSolutionId]: {
          ms: Number(moduleTime.ms || 0) + (isTeamTime ? 0 : addTimeInApp),
          togetherMs: Number(moduleTime.togetherMs || 0) + (isTeamTime ? addTimeInApp : 0),
        },
      };
    }

    if (existingIndex >= 0) timeInApp[existingIndex] = current;
    else timeInApp.push(current);
    return timeInApp;
  };

  // Mobile record keeping is local-first. Assessment submission remains online because it
  // controls Placement progression, but reports/time are uploaded only by weekly Call Home.
  if (isMobileAppRuntime()) {
    try {
      const { learnerId, data } = updateLearnerReports(learner);
      const updatedLearner = {
        ...learner,
        ...data,
        timeInApp: addLocalTime(learner, !!(team && team.length)),
      };
      dispatch({ filter, type: UPDATE_LEARNER, payload: updatedLearner });

      try {
        await submitDesktopAssessmentResultFromCloseActivity({ getState, learnerId, stateSnapshot });
      } catch (assessmentError) {
        console.error('[desktop-assessment][action-submit-error]', assessmentError);
      }

      if (team && team.length) {
        const updatedTeam = team.map((member) => {
          const memberUpdate = updateLearnerReports(member).data;
          const next = {
            ...member,
            ...memberUpdate,
            timeInApp: addLocalTime(member, true),
          };
          if (isMobileLocalFriend(member)) {
            return updateMobileFamilyFriend(member._id, next) || next;
          }
          return next;
        });
        dispatch({ filter, type: UPDATE_TEAM, payload: updatedTeam });
      }

      out();
    } catch (err) {
      console.error('[flinkPlayActions][closeActivity][mobile-local-save-error]', err);
      alert('Error while saving family member record data');
    }
    return;
  }

  if (learningCenter?.config?.noReports) {
    return out();
  }

  try {
    const { learnerId, data } = updateLearnerReports(learner);
    await dispatch(updateLearner(learnerId, data));

    try {
      await submitDesktopAssessmentResultFromCloseActivity({ getState, learnerId, stateSnapshot });
    } catch (assessmentError) {
      console.error('[desktop-assessment][action-submit-error]', assessmentError);
    }

    if (team && team.length) {
      const serverTeam = team.filter((member) => !isMobileLocalFriend(member));
      const localTeam = team.filter(isMobileLocalFriend).map((member) => {
        const { data: memberData } = updateLearnerReports(member);
        return updateMobileFamilyFriend(member._id, memberData) || { ...member, ...memberData };
      });

      if (serverTeam.length) {
        await updateTeam(serverTeam.map(updateLearnerReports))(dispatch);
      }

      if (localTeam.length) {
        const refreshedServerTeam = (getState().flinkPlay.team || [])
          .filter((member) => !isMobileLocalFriend(member));
        dispatch({
          filter,
          type: UPDATE_TEAM,
          payload: [...refreshedServerTeam, ...localTeam],
        });
      }
    }

    if (addTimeInApp < 24 * 60 * 60 * 1000) {
      await trackTime(addTimeInApp)(dispatch);
    }

    out();
  } catch (err) {
    console.error('[flinkPlayActions][closeActivity][save-error]', err);
    alert('Error while saving family member record data');
  }
};

export const removeLearnerReports = () => async (dispatch, getState) => {
  const {
    flinkPlay: { learner },
  } = getState();

  const learnerUpdate = {
    reports: {},
  };

  try {
    await dispatch(updateLearner(learner._id, learnerUpdate));
  } catch (err) {
    alert('Error while saving family member record data');
  }
};

export const initializeFlinkPlaySolution = () => async (dispatch, getState) => settingsClosedHandler(dispatch, getState);

const settingsClosedHandler = async (dispatch, getState) => {
  const {
    common: { locales, strings },
    flinkPlay: {
      solution,
      solutionMenu: currentSolutionMenu,
      solutionLocale: currentSolutionLocale,
      activityLocale: currentActivityLocale,
      product,
      family,
      learner: { settings, type: learnerType, familyId, isMobileUser, customContentOnboarding },
    },
    status: { lang },
  } = getState();
  
  /*const resultPrepaidStatus = await api.family.getPrepaidStatus(familyId);
  let solutionMulti = [];
  const multiProduct = !!resultPrepaidStatus?.purchases?.length;
  if (multiProduct) {
    const productIds = resultPrepaidStatus.purchases.map(item => item.productId);
    const result = await api.products.getMultiProductSolutions(productIds);
    solutionMulti = result
    .flatMap(p => p.solutions)
    .filter((s, index, self) =>
      index === self.findIndex(x => x._id.toString() === s._id.toString())
      );
  }*/

  // Mobile wrapper users are onboarded into the configured Custom Content pipeline
  // without a purchase/PrepaidStatus record. The prepaid request below only exists
  // to resolve additional purchased products for the desktop paid-family flow.
  const isMobileFlow = Boolean(family?.isMobileCustomer || isMobileUser);
  const resultPrepaidStatus = isMobileFlow
    ? null
    : await api.family.getPrepaidStatus(familyId);

  if (isMobileFlow) {
    console.log('[flinkPlayActions][prepaidStatus][skip-mobile]', {
      familyId,
      isMobileCustomer: Boolean(family?.isMobileCustomer),
      isMobileUser: Boolean(isMobileUser),
    });
  }

  // --- normalize api response for getMultiProductSolutions ---
  const unwrapMultiProductSolutions = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;

    // common shapes:
    if (Array.isArray(resp.data)) return resp.data;                 // axios-like
    if (Array.isArray(resp?.data?.data)) return resp.data.data;     // {data:{data:[]}}
    if (Array.isArray(resp.products)) return resp.products;
    if (Array.isArray(resp?.data?.products)) return resp.data.products;

    return [];
  };

  const purchasesArr = Array.isArray(resultPrepaidStatus?.purchases)
    ? resultPrepaidStatus.purchases
    : [];

  // normalize productId (string or {_id})
  const productIds = [...new Set(
    purchasesArr
      .map((x) => x?.productId?._id || x?.productId)
      .filter(Boolean)
      .map((x) => String(x))
  )];

  // multi-product only when there are 2+ distinct products
  const multiProduct = productIds.length > 1;

  let solutionMulti = [];
  if (multiProduct) {
    const resp = await api.products.getMultiProductSolutions(productIds);
    const list = unwrapMultiProductSolutions(resp);

    console.log('[flinkPlayActions][multiProductSolutions]', {
      productIds,
      respType: Array.isArray(resp) ? 'array' : typeof resp,
      listLen: list.length,
    });

    solutionMulti = list
      .flatMap((p) => (Array.isArray(p?.solutions) ? p.solutions : []))
      .filter(Boolean)
      .filter((s, index, self) =>
        index === self.findIndex(x => String(x?._id) === String(s?._id))
      );
  }

  const multiProductMode = multiProduct && solutionMulti.length > 0;

  console.log('[flinkPlayActions][prepaidStatus]', {
    purchasesLen: purchasesArr.length,
    productIds,
    multiProduct,
    multiProductMode,
    solutionMultiLen: solutionMulti.length,
  });   
  
  console.log('settingsClosedHandler resultPrepaidStatus: ', resultPrepaidStatus);
  console.log('settingsClosedHandler solution: ', solution);
  console.log('settingsClosedHandler solutionMulti: ', solutionMulti);

  const selectedSolutionId = settings?.selectedSolutionId || null;
  // Settings is also closed inside an already generated Custom Level. Do not
  // reinitialize its source Solution with the Login locale and replace the
  // generated menu or translator. Switching to a different Solution still uses
  // the normal initialization path below.
  if (isMobileFlow && currentSolutionMenu?.generatedCustomContentLevelId
    && (!selectedSolutionId || String(solution?._id || '') === String(selectedSolutionId))) {
    return;
  }
  const mobileRuntimeLanguageCode = isMobileFlow
    ? normalizePlacementLanguageCode(lang?.code)
    : '';
  const mobileInstructionLanguageCode = isMobileFlow
    ? normalizePlacementLanguageCode(customContentOnboarding?.instructionLanguage)
    : '';
  const currentSolutionLanguage = solution ? normalizeSolutionLanguageMode(solution) : null;
  const expectedMobileActivityLocaleCode = isMobileFlow
    && currentSolutionLanguage?.languageMode === 'transition'
    ? currentSolutionLanguage.activityLocaleCode
    : (mobileInstructionLanguageCode || mobileRuntimeLanguageCode);
  const mobileLanguageChanged = Boolean(
    mobileRuntimeLanguageCode
    && (
      normalizePlacementLanguageCode(currentSolutionLocale?.code) !== mobileRuntimeLanguageCode
      || normalizePlacementLanguageCode(currentActivityLocale?.code) !== expectedMobileActivityLocaleCode
    )
  );

  if (!solution || (solution && solution._id !== selectedSolutionId) || mobileLanguageChanged) {
    const { defaultSolutionsForAdmins, defaultSolutionsForMembers } = product;
    const defaultSolutions =
      (learnerType === 'adult'
        ? defaultSolutionsForAdmins
        : defaultSolutionsForMembers) || {};

    console.debug('settingsClosedHandler', {
      defaultSolutions,
      learnerType,
      lang,
    });

    //const solutionMultiByLang = multiProduct ? solutionMulti.filter(item => item.locale === lang.code) : [];

    //const solution = _.find(!multiProduct ? product.solutions : solutionMulti, { _id: settings.selectedSolutionId }) ||
    //  _.find(product.solutions, { _id: !multiProduct ? defaultSolutions[lang.code] : solutionMultiByLang[0]._id }) ||
    //  _.first(!multiProduct ? product.solutions : solutionMulti);

    const solutionMultiByLang = multiProductMode
      ? solutionMulti.filter(item => item.locale === lang.code)
      : [];

    const pool = !multiProductMode ? product.solutions : solutionMulti;

    const fallbackId = !multiProductMode
      ? defaultSolutions[lang.code]
      : (solutionMultiByLang[0]?._id);

    const solution =
      _.find(pool, { _id: selectedSolutionId }) ||
      (fallbackId ? _.find(pool, { _id: fallbackId }) : null) ||
      _.first(pool);

    if (!solution)
      return alert('Product does not have solutions');

    const normalizedLanguage = normalizeSolutionLanguageMode(solution);
    // Preserve the existing Login/UI runtime language behavior first.
    // The add-on below only separates Activity content when Mobile Registration
    // supplied an explicit instruction language.
    const existingInterfaceLocaleCode = mobileRuntimeLanguageCode
      || normalizedLanguage.interfaceLocaleCode;
    // Transition is an English Activity solution with localized Help. Login/UI
    // language must never turn it into an ES/VM Activity solution. This is also
    // the language contract used by the offline prebuilt resolver.
    const preserveTransitionContent = isMobileFlow
      && normalizedLanguage.languageMode === 'transition';
    const existingActivityLocaleCode = preserveTransitionContent
      ? normalizedLanguage.activityLocaleCode
      : (mobileRuntimeLanguageCode || normalizedLanguage.activityLocaleCode);
    const existingLanguageMode = preserveTransitionContent
      ? 'transition'
      : (mobileRuntimeLanguageCode ? 'immersion' : normalizedLanguage.languageMode);
    const mobileInstructionLanguageAddon = applyMobileInstructionLanguageAddon({
      enabled: isMobileFlow && !preserveTransitionContent,
      instructionLanguage: customContentOnboarding?.instructionLanguage,
      interfaceLocaleCode: existingInterfaceLocaleCode,
      activityLocaleCode: existingActivityLocaleCode,
      languageMode: existingLanguageMode,
    });
    const { interfaceLocaleCode, activityLocaleCode, languageMode } = mobileInstructionLanguageAddon;
    const solutionLocale = findLocaleOrFallback(locales, interfaceLocaleCode);
    const activityLocale = findLocaleOrFallback(locales, activityLocaleCode);

    if (isMobileFlow && mobileInstructionLanguageAddon.requestedInstructionLanguage) {
      appendPlacementLocaleDiagnostic('runtime', {
        loginLocale: existingInterfaceLocaleCode,
        previousActivityLocale: existingActivityLocaleCode,
        requestedInstructionLanguage: mobileInstructionLanguageAddon.requestedInstructionLanguage,
        resolvedActivityLocale: activityLocale?.code || null,
        applied: mobileInstructionLanguageAddon.applied,
      });
    }
    // The small web-only common bootstrap initially includes English and the
    // login locale. Other Solution locales are fetched before switching the
    // translator; never substitute English for a missing Spanish/VM locale.
    if (!isMobileAppRuntime()) {
      try {
        await ensureWebPlayLocaleStrings(solutionLocale.code)(dispatch, getState);
      } catch (error) {
        console.error('[web-play-runtime] failed to load Solution locale', solutionLocale.code, error);
        alert('Unable to load the selected language. Please retry when connected.');
        return;
      }
    }
    const effectiveStrings = getState().common?.strings || strings;
    const solutionTranslate = getTranslateFunc(effectiveStrings, solutionLocale.code);
    const normalizedSolution = {
      ...solution,
      locale: interfaceLocaleCode,
      languageMode,
      activityLocale: activityLocale.code,
    };

    const { solutionMenu: solutionMenuMeta } = solution || {};

    if (solution && !solutionMenuMeta) {
      return alert('Solution does not have menu');
    }

    try {
      const currentState = getState().flinkPlay || {};
      const mobileBootstrap = currentState.mobileBootstrap || {};
      const canUseMobileBootstrapMenu = Boolean(
        isMobileAppRuntime()
        && mobileBootstrap.solutionMenu
        && String(mobileBootstrap.solutionId || '') === String(solution._id || '')
        && Array.isArray(mobileBootstrap.solutionMenu.activityGroups)
        && Array.isArray(mobileBootstrap.solutionMenu.activities)
      );
      const solutionMenu = canUseMobileBootstrapMenu
        ? mobileBootstrap.solutionMenu
        : await getSolutionMenu(solution._id)(dispatch, getState);

      if (canUseMobileBootstrapMenu) {
        console.info('[mobile-play-bootstrap][solution-menu-cache-hit]', {
          solutionId: String(solution._id),
          activityGroupCount: solutionMenu.activityGroups.length,
          activityCount: solutionMenu.activities.length,
        });
      }

      // Async menu fetch may finish after first-time Custom Level creation.
      // Never let an older Settings initialization overwrite the generated
      // menu's freshly selected Solution locale and Activity language.
      if (isMobileFlow && getState().flinkPlay?.solutionMenu?.generatedCustomContentLevelId) {
        return;
      }

      dispatch({
        filter,
        type: SET_SOLUTION,
        payload: {
          solution: normalizedSolution,
          solutionMenu,
          solutionLocale,
          activityLocale,
          solutionTranslate,
        },
      });
    } catch (err) {
      alert(err || 'Error while fetching solution`s menu');
      dispatch({
        filter,
        type: SET_SOLUTION,
        payload: { solution: null, solutionMenu: null },
      });
    }
  }
};

// When supported, compress the *transport*, not the Activity/WordAG data.
// Browser-native DecompressionStream avoids a new dependency and keeps the
// resulting Redux solutionMenu shape strictly identical to the legacy API.
const canDecodeWebPlayMenu = () => !isMobileAppRuntime()
  && typeof window !== 'undefined'
  && typeof window.DecompressionStream === 'function'
  && typeof Blob === 'function'
  && typeof Response === 'function'
  && typeof atob === 'function';

const decodeWebPlayMenu = async (payload) => {
  if (payload?.codec !== 'gzip-base64') return payload;
  const bytes = Uint8Array.from(atob(payload.data || ''), char => char.charCodeAt(0));
  const stream = new Blob([bytes]).stream().pipeThrough(new window.DecompressionStream('gzip'));
  const json = await new Response(stream).text();
  return JSON.parse(json);
};

export const getSolutionMenu = (solutionId, params) => (dispatch) =>
  withLoading(dispatch, async () => {
    const webPlay = !isMobileAppRuntime();
    const baseParams = webPlay ? { ...params, webOptimized: 1 } : params;
    const compressed = webPlay && canDecodeWebPlayMenu();
    try {
      const response = await axios.get(`/api/flink-play/get-solution-menu/${solutionId}`, {
        params: compressed ? { ...baseParams, responseCodec: 'gzip-base64' } : baseParams,
      });
      try {
        return await decodeWebPlayMenu(response.data);
      } catch (decodeError) {
        // An unsupported/bad transfer codec must not strand a family session.
        // Retry once with the unchanged JSON endpoint.
        console.warn('[web-play-menu] compressed menu failed; retrying JSON', decodeError);
        const legacy = await axios.get(`/api/flink-play/get-solution-menu/${solutionId}`, {
          params: baseParams,
        });
        return legacy.data;
      }
    } catch (err) {
      return Promise.reject(err.response?.data || err);
    }
  });

const GENERATED_CUSTOM_CONTENT_SUBJECTS = ['phonics', 'reading', 'vocabulary', 'writing', 'feelings'];

const isGeneratedCustomContentRuntimeTreeComplete = (solutionMenu = {}, generatedLevel = {}) => {
  const activityGroups = Array.isArray(solutionMenu.activityGroups) ? solutionMenu.activityGroups : [];
  const activities = Array.isArray(solutionMenu.activities) ? solutionMenu.activities : [];
  const groupById = activityGroups.reduce((result, group) => {
    result[String(group?._id || '')] = group;
    return result;
  }, {});
  const activityIds = new Set(activities.map((activity) => String(activity?._id || '')).filter(Boolean));
  const subjectPlans = generatedLevel?.contentPlan?.subjects || {};
  const readingPlan = subjectPlans.reading || {};
  const readingRootId = String((readingPlan.rootGroupIds || [])[0] || '');
  const selectedReadingGroupIds = (readingPlan?.selection?.selectedBooks || [])
    .map((book) => String(book?.bookGroupId || ''))
    .filter(Boolean);
  const runtimeTreeRootIds = Object.entries(subjectPlans)
    .reduce((result, [subject, subjectPlan]) => {
      if (subject === 'reading') return result;
      return result.concat(Array.isArray(subjectPlan?.rootGroupIds) ? subjectPlan.rootGroupIds : []);
    }, [])
    .map((groupId) => String(groupId || ''))
    .filter(Boolean)
    .concat(selectedReadingGroupIds);
  const visitedGroupIds = new Set();

  if (readingRootId && !groupById[readingRootId]) return false;

  const visitGroup = (groupId) => {
    const normalizedId = String(groupId || '');
    if (!normalizedId || visitedGroupIds.has(normalizedId)) return true;

    const group = groupById[normalizedId];
    if (!group) return false;
    visitedGroupIds.add(normalizedId);

    return (group.elements || []).every((element) => {
      const elementId = String(element?._id || '');
      if (!elementId) return true;
      return element.isGroup ? visitGroup(elementId) : activityIds.has(elementId);
    });
  };

  return runtimeTreeRootIds.every(visitGroup);
};

export const buildGeneratedCustomContentSolutionMenu = (sourceSolutionMenu = {}, generatedLevel = {}, options = {}) => {
  const subjectPlans = generatedLevel?.contentPlan?.subjects || {};
  const customLevelIndex = Math.max(1, Number(generatedLevel?.customLevelIndex || generatedLevel?.contentPlan?.customLevelIndex || 1));
  const customLevelLabel = `Custom Level ${customLevelIndex}`;
  const tolerateMissingAuthoredElements = Boolean(options?.tolerateMissingAuthoredElements);
  const activityGroups = Array.isArray(sourceSolutionMenu.activityGroups) ? sourceSolutionMenu.activityGroups : [];
  const activities = Array.isArray(sourceSolutionMenu.activities) ? sourceSolutionMenu.activities : [];
  const menuGroups = Array.isArray(sourceSolutionMenu.menuGroups) ? sourceSolutionMenu.menuGroups : [];
  const groupById = activityGroups.reduce((acc, group) => {
    acc[String(group._id)] = group;
    return acc;
  }, {});
  const activityById = activities.reduce((acc, activity) => {
    acc[String(activity._id)] = activity;
    return acc;
  }, {});
  const rootOverrides = {};
  const readingPlan = subjectPlans.reading || {};
  const readingRootId = String((readingPlan.rootGroupIds || [])[0] || '');
  const selectedBooks = readingPlan?.selection?.selectedBooks || [];

  if (readingRootId) {
    const readingRoot = groupById[readingRootId];
    const selectedBookIds = new Set(selectedBooks.map((book) => String(book.bookGroupId)));

    if (!readingRoot || !selectedBookIds.size) {
      throw new Error(`${customLevelLabel} has no selectable Reading books.`);
    }

    const authoredReadingElementsById = (readingRoot.elements || []).reduce((result, element) => {
      if (element.isGroup) {
        result[String(element._id)] = element;
      }
      return result;
    }, {});

    // Keep the generated snapshot order. Filtering the authored root elements preserves
    // the Make/source order and incorrectly places priority sections after filler books.
    const readingElements = selectedBooks
      .map((book) => {
        const bookGroupId = String(book.bookGroupId);
        return authoredReadingElementsById[bookGroupId]
          || (groupById[bookGroupId] ? { _id: bookGroupId, isGroup: true } : null);
      })
      .filter(Boolean);

    if (!readingElements.length || readingElements.length !== selectedBookIds.size) {
      throw new Error('Selected Reading books were not hydrated into the generated Reading menu.');
    }

    rootOverrides[readingRootId] = {
      ...readingRoot,
      elements: readingElements,
    };
  }

  const includedGroups = [];
  const includedActivities = [];
  const includedGroupIds = new Set();
  const includedActivityIds = new Set();

  const includeGroupTree = (groupId) => {
    const normalizedId = String(groupId || '');
    if (!normalizedId || includedGroupIds.has(normalizedId)) return;

    const group = rootOverrides[normalizedId] || groupById[normalizedId];
    if (!group) {
      throw new Error(`Authored group ${normalizedId} is unavailable in ${customLevelLabel} runtime menu.`);
    }

    includedGroupIds.add(normalizedId);
    const includedGroupIndex = includedGroups.length;
    includedGroups.push(group);
    const runtimeElements = [];

    (group.elements || []).forEach((element) => {
      const elementId = String(element?._id || '');
      if (!elementId) return;

      if (element.isGroup) {
        const childGroup = rootOverrides[elementId] || groupById[elementId];
        if (!childGroup && tolerateMissingAuthoredElements) {
          console.warn('[mobile-custom-content][orphan-authored-group-skipped]', {
            customLevelLabel,
            parentGroupId: normalizedId,
            groupId: elementId,
          });
          return;
        }

        includeGroupTree(elementId);
        runtimeElements.push(element);
        return;
      }

      if (includedActivityIds.has(elementId)) {
        runtimeElements.push(element);
        return;
      }

      const activity = activityById[elementId];
      if (!activity) {
        if (tolerateMissingAuthoredElements) {
          console.warn('[mobile-custom-content][orphan-authored-activity-skipped]', {
            customLevelLabel,
            parentGroupId: normalizedId,
            activityId: elementId,
          });
          return;
        }
        throw new Error(`Authored activity ${elementId} is unavailable in ${customLevelLabel} runtime menu.`);
      }
      includedActivityIds.add(elementId);
      includedActivities.push(activity);
      runtimeElements.push(element);
    });

    if (tolerateMissingAuthoredElements) {
      includedGroups[includedGroupIndex] = {
        ...group,
        elements: runtimeElements,
      };
    }
  };

  const customMenuGroups = GENERATED_CUSTOM_CONTENT_SUBJECTS.filter((subject) => subjectPlans[subject]).map((subject) => {
    const rootGroupId = String((subjectPlans[subject]?.rootGroupIds || [])[0] || '');
    if (!rootGroupId) {
      throw new Error(`${customLevelLabel} is missing the ${subject} authored group.`);
    }

    includeGroupTree(rootGroupId);
    const menuGroup = menuGroups.find((item) => String(item.groupId) === rootGroupId);
    if (!menuGroup) {
      throw new Error(`${customLevelLabel} cannot expose the ${subject} group in the authored menu.`);
    }

    return {
      ...menuGroup,
      menuLevel: String(customLevelIndex),
    };
  });

  return {
    ...sourceSolutionMenu,
    productId: null,
    menuTitle: null,
    menuSubtitle: null,
    menuGroups: customMenuGroups,
    activityGroups: includedGroups,
    activities: includedActivities,
    generatedCustomContentLevelId: generatedLevel._id,
    generatedCustomLevelIndex: generatedLevel.customLevelIndex || 1,
    generatedCustomContentAgeBand: generatedLevel.ageBand || null,
    generatedCustomContentContextKey:
      generatedLevel?.contentPlan?.generatorSettingsSnapshot?.contextKey || null,
  };
};

export const activateGeneratedCustomContentMenu = (generatedLevel) => async (dispatch, getState) => {
  const sourceSolutionId = generatedLevel?.contentPlan?.sourceSolution?._id || generatedLevel?.sourceSolutionId;
  if (!sourceSolutionId) {
    throw new Error(`Custom Level ${generatedLevel?.customLevelIndex || 1} source solution is missing.`);
  }

  const currentBeforeMenu = getState().flinkPlay || {};
  const mobileBootstrap = currentBeforeMenu.mobileBootstrap || {};
  const bootstrapGeneratedLevelId = mobileBootstrap.generatedCustomContentLevel?._id || null;
  const generatedSubjectRootGroupIds = Object.values(generatedLevel?.contentPlan?.subjects || {})
    .reduce((result, subjectPlan) => (
      result.concat(Array.isArray(subjectPlan?.rootGroupIds) ? subjectPlan.rootGroupIds : [])
    ), [])
    .map((groupId) => String(groupId || ''))
    .filter(Boolean);
  const generatedReadingGroupIds = (generatedLevel?.contentPlan?.subjects?.reading?.selection?.selectedBooks || [])
    .map((book) => String(book?.bookGroupId || ''))
    .filter(Boolean);
  const generatedRuntimeGroupIds = [...new Set([
    ...generatedSubjectRootGroupIds,
    ...generatedReadingGroupIds,
  ])];
  const bootstrapActivityGroupIds = new Set(
    (mobileBootstrap.solutionMenu?.activityGroups || []).map((group) => String(group?._id || ''))
  );
  const bootstrapHasGeneratedRuntimeGroups = generatedRuntimeGroupIds.every(
    (groupId) => bootstrapActivityGroupIds.has(groupId)
  );
  const bootstrapHasCompleteGeneratedRuntimeTree = Boolean(
    bootstrapHasGeneratedRuntimeGroups
    && isGeneratedCustomContentRuntimeTreeComplete(mobileBootstrap.solutionMenu, generatedLevel)
  );
  const canUseMobileBootstrapMenu = Boolean(
    isMobileAppRuntime()
    && mobileBootstrap.solutionMenu
    && String(mobileBootstrap.solutionId || '') === String(sourceSolutionId || '')
    && String(bootstrapGeneratedLevelId || '') === String(generatedLevel?._id || '')
    && bootstrapHasCompleteGeneratedRuntimeTree
    && Array.isArray(mobileBootstrap.solutionMenu.activityGroups)
    && Array.isArray(mobileBootstrap.solutionMenu.activities)
  );
  const sourceSolutionMenu = canUseMobileBootstrapMenu
    ? mobileBootstrap.solutionMenu
    : await getSolutionMenu(sourceSolutionId, {
      generatedCustomContentLevelId: generatedLevel._id,
    })(dispatch, getState);

  if (canUseMobileBootstrapMenu) {
    console.info('[mobile-play-bootstrap][generated-menu-cache-hit]', {
      sourceSolutionId: String(sourceSolutionId),
      generatedCustomContentLevelId: String(generatedLevel._id),
    });
  }

  const generatedSolutionMenu = buildGeneratedCustomContentSolutionMenu(sourceSolutionMenu, generatedLevel, {
    tolerateMissingAuthoredElements: isMobileAppRuntime(),
  });
  const playState = getState();
  const current = playState.flinkPlay;
  // A newly generated Custom Level does not run Settings initialization again.
  // Sync its presentation locale here, before Intro/Help/Menu renders. This must
  // never change the Activity locale: Transition retains authored EN content.
  const requestedInstructionCode = isMobileAppRuntime()
    ? normalizePlacementLanguageCode(
      generatedLevel?.instructionLanguage
      || generatedLevel?.contentPlan?.instructionLanguage
      || current.learner?.customContentOnboarding?.instructionLanguage
    )
    : '';
  // The source Solution defines the Custom Level UI language. The Login screen
  // can still hold EN during the first creation, so never let status.lang
  // overwrite the authored presentation language here. Instruction language
  // selects Activity content separately (and Transition retains its authored
  // Activity locale).
  const sourceSolution = generatedLevel?.contentPlan?.sourceSolution || {};
  const requestedPresentationCode = isMobileAppRuntime()
    ? normalizePlacementLanguageCode(
      sourceSolution?.locale
      || generatedLevel?.explanationLanguage
      || generatedLevel?.contentPlan?.explanationLanguage
      || current.learner?.customContentOnboarding?.explanationLanguage
      || current.solution?.locale
      || current.solutionLocale?.code
      || playState.status?.lang?.code
      || requestedInstructionCode
    )
    : '';
  const authoredUILocale = requestedPresentationCode
    ? (playState.common?.locales || []).find((locale) => (
      normalizePlacementLanguageCode(locale?.code) === requestedPresentationCode
    ))
    : null;
  const preserveTransitionContent = isMobileAppRuntime()
    && (String(sourceSolution?.languageMode || current.solution?.languageMode || '').toLowerCase() === 'transition');
  const authoredActivityLocale = isMobileAppRuntime() && !preserveTransitionContent && requestedInstructionCode
    ? (playState.common?.locales || []).find((locale) => (
      normalizePlacementLanguageCode(locale?.code) === requestedInstructionCode
    ))
    : null;
  const nextSolutionLocale = authoredUILocale || current.solutionLocale;
  const nextActivityLocale = authoredActivityLocale || current.activityLocale;
  const localeNeedsSync = Boolean(
    (authoredUILocale && normalizePlacementLanguageCode(current.solutionLocale?.code) !== requestedPresentationCode)
    || (authoredActivityLocale && normalizePlacementLanguageCode(current.activityLocale?.code) !== requestedInstructionCode)
  );
  // A generated menu may inherit a stale EN translator from Placement even
  // when solutionLocale.code happens to match. Rebuild it from the same locale.
  const nextSolutionTranslate = authoredUILocale && Array.isArray(playState.common?.strings)
    ? getTranslateFunc(playState.common.strings, authoredUILocale.code)
    : current.solutionTranslate;

  if (localeNeedsSync) {
    console.info('[mobile-custom-content][presentation-locale-synced]', {
      generatedCustomContentLevelId: String(generatedLevel?._id || ''),
      previousLocale: current.solutionLocale?.code || '',
      presentationLocale: nextSolutionLocale.code,
      previousActivityLocale: current.activityLocale?.code || '',
      activityLocale: nextActivityLocale?.code || '',
      requestedInstructionCode,
    });
  }

  dispatch({
    filter,
    type: SET_SOLUTION,
    payload: {
      solution: current.solution && (authoredUILocale || authoredActivityLocale)
        ? {
          ...current.solution,
          ...(authoredUILocale ? { locale: nextSolutionLocale.code } : {}),
          ...(authoredActivityLocale ? { activityLocale: nextActivityLocale.code } : {}),
        }
        : current.solution,
      solutionMenu: generatedSolutionMenu,
      solutionLocale: nextSolutionLocale,
      activityLocale: nextActivityLocale,
      solutionTranslate: nextSolutionTranslate,
      currentActivityGroup: null,
      groupsBreadcrumbs: [],
      activity: null,
      familyReportsOpened: false,
    },
  });

  // The Custom Level menu above exists in Redux immediately, but offline Activity
  // loading resolves Activity metadata from the persisted mobile bootstrap. Keep
  // those two views of the active menu identical; otherwise a downloaded Custom
  // Level can render its menu but its Activities cannot be resolved after network
  // is removed.
  if (isMobileAppRuntime()) {
    try {
      const cached = await getCachedPlayBootstrap();
      if (cached) {
        await cachePlayBootstrap({
          ...cached,
          mobileBootstrap: {
            ...(cached.mobileBootstrap || {}),
            ...(currentBeforeMenu.mobileBootstrap || {}),
            solutionId: sourceSolutionId,
            solutionMenu: generatedSolutionMenu,
            generatedCustomContentLevel: generatedLevel,
          },
        });
      }
    } catch (error) {
      console.warn('[mobile-custom-content][generated-menu-cache-save-failed]', {
        generatedCustomContentLevelId: generatedLevel?._id || null,
        error: error?.message || String(error),
      });
    }
  }

  return generatedSolutionMenu;
};

export const closeSettings = () => (dispatch, getState) => {
  settingsClosedHandler(dispatch, getState);

  dispatch({
    filter,
    type: CLOSE_SETTINGS,
  });
};

export const setMenu = (menu) => (dispatch, getState) => {
  const {
    flinkPlay: { learner, solutionMenu },
  } = getState();

  dispatch({
    filter,
    type: SET_CURRENT_MENU,
    payload: menu,
  });

  // Generated Custom Level navigation must not auto-open Menu Help. The authored
  // Help button remains available; only the automatic first-visit popup is
  // suppressed for the generated runtime.
  const generatedCustomContentRuntime = Boolean(
    solutionMenu?.generatedCustomContentLevelId
    || solutionMenu?.generatedCustomLevelIndex != null
  );
  const shouldAutoShowHelp = !generatedCustomContentRuntime
    && shouldShowMenuHelpAutomatically(menu, learner);
  const menuWasAlreadyShown = Boolean(
    menu && learner && (learner.menusShown || []).some((shownMenuId) =>
      toIdString(shownMenuId) === toIdString(menu._id)
    )
  );

  if (menu && learner && !menuWasAlreadyShown) {
    const nextMenusShown = [...(learner.menusShown || []), menu._id];
    const mobileOffline = isMobileAppRuntime() && !isOnline();

    if (mobileOffline) {
      // Opening a MenuGroup must never call /update-learners while offline.
      // That request previously failed and updateLearner() displayed the native
      // OK dialog every time an unseen subject/menu was opened. Keep the menu
      // state locally for the current offline session instead.
      dispatch({
        filter,
        type: UPDATE_LEARNER,
        payload: {
          ...learner,
          menusShown: nextMenusShown,
        },
      });
    } else {
      const silentMenuSave =
        isMobileAppRuntime() && getMobileAppPlatform() === 'android';

      dispatch(
        updateLearner(
          learner._id,
          { menusShown: nextMenusShown },
          { silent: silentMenuSave }
        )
      );
    }
  }

  dispatch(setMenuButtons((menu && menu.globalButtons) || [GO_BACK]));

  if (shouldAutoShowHelp) {
    dispatch(showMenuHelp());
  }
};

export const showMultiplayerHelp = () => async (dispatch, getState) => {
  const {
    flinkPlay: { solutionLocale },
    status: { translate },
    flinkPlay: { solutionTranslate },
  } = getState();

  console.debug('Showing multiplayer help for locale', solutionLocale);
  const localeFolder = getLocaleAudioFolderName(solutionLocale);
  const audioHelp = `${audioURL}/Generic/ActivityHelp/${localeFolder}/multiplayermodehelp.mp3`;

  dispatch(
    showHelp({
      data: {},
      stringNumber: 987,
      audioHelp,
      customTranslate: solutionTranslate || translate,
    })
  );
};

export const showLoginFlinkPlayHelp = (options = {}) => (dispatch, getState) => {
  const {
    status: { translate, lang },
    common: { strings, locales = [] },
  } = getState();

  const langCode = lang?.code || 'en';
  const requestedStringNumber = Number(options?.stringNumber);
  const loginHelpStringNumber = Number.isFinite(requestedStringNumber)
    ? requestedStringNumber
    : 988;

  const string = findStringByNumber(strings, loginHelpStringNumber);
  const normalizedLangCode = String(langCode || 'en').trim() || 'en';
  const baseLangCode = normalizedLangCode.split('-')[0];
  const loginAudioLocaleCodes = [...new Set([normalizedLangCode, baseLangCode].filter(Boolean))];
  let localizedAudio = { filename: '', localeCode: normalizedLangCode };

  for (const localeCode of loginAudioLocaleCodes) {
    const localizedString = _.get(string, ['data', localeCode]);
    const filename = localizedString?.stringAudio || localizedString?.audio;
    if (filename) {
      localizedAudio = { filename: String(filename).trim(), localeCode };
      break;
    }
  }

  const resolvedLocale = _.find(locales, { code: localizedAudio.localeCode })
    || (localizedAudio.localeCode === normalizedLangCode ? lang : null)
    || _.find(locales, { code: baseLangCode })
    || lang
    || { code: normalizedLangCode, name: normalizedLangCode };
  const stringAudioUrl = buildStringAudioUrl(localizedAudio.filename, resolvedLocale);
  const legacyLoginAudio = `${audioURL}/Generic/LoginHelp/${getLocaleAudioFolderName(lang || resolvedLocale)}/Online/loginhelpdialog.mp3`;
  const audioHelp = stringAudioUrl || legacyLoginAudio;
  const loginTranslate = getTranslateFunc(strings, normalizedLangCode);

  // Keep preload non-blocking so opening Help does not wait on the request.
  window.audioPlayer?.preload?.(audioHelp);

  // On iOS/Android the first play must stay inside the Help button's user
  // gesture. Starting it after the modal mounts loses that gesture in the
  // native WebView. Login audio is already preloaded before controls appear.
  let audioStartedOnOpen = false;
  if (
    audioHelp
    && isMobileAppRuntime()
    && typeof window.audioPlayer?.playSrc === 'function'
  ) {
    try {
      window.audioPlayer?.playSrc?.(audioHelp);
      audioStartedOnOpen = true;
    } catch (error) {
      console.error('[FLC mobile][Login Help audio]', error);
    }
  }

  dispatch(
    showHelp({
      data: {},
      stringNumber: loginHelpStringNumber,
      audioHelp,
      audioStartedOnOpen,
      customTranslate: loginTranslate || translate,
      localeCode: normalizedLangCode,
      audioDebugContext: `Login Help | String #${loginHelpStringNumber} | Locale ${normalizedLangCode}`,
    })
  );
}

export const showFamilyPointsHistory = () => (dispatch) =>
  withLoading(dispatch, () =>
    axios
      .get('/api/flink-play/get-family-points-history')
      .then((response) => {
        dispatch({
          filter,
          type: SHOW_FAMILY_POINTS_HISTORY,
          payload: response.data,
        })
      })
  );

export const closeFamilyPointsHistory = () => ({
  filter,
  type: CLOSE_FAMILY_POINTS_HISTORY,
});

export const checkWriting = (writing) => (dispatch) =>
  withLoading(dispatch, () =>
    axios
      .post('/api/ai/check-writing', { writing })
      .then((response) => {
        dispatch({
          filter,
          type: OPEN_WRITING_FEEDBACK,
          payload: response.data.feedback,
        });
      })
      .catch((err) => {
        console.error(err);
        return Promise.reject(err.response.data);
      })
  );

export const closeWritingFeedback = () => ({
  filter,
  type: CLOSE_WRITING_FEEDBACK,
});

export const showMenuHelp = ({ compactMobile = false, instantMobile = false, localeOverride = null, customTranslate = null } = {}) => async (dispatch, getState) => {
  const {
    flinkPlay: {
      currentMenu,
      solutionLocale: stateSolutionLocale,
      currentActivityGroup,
      solutionMenu: { activityGroups },
    },
    common: { strings, locales = [] },
  } = getState();

  if (!currentMenu) return;

  const solutionLocale = localeOverride?.code
    ? localeOverride
    : stateSolutionLocale;
  if (!solutionLocale?.code) return;

  const instantNativeMobile = Boolean(
    instantMobile
    && compactMobile
    && isMobileAppRuntime()
  );
  const helpRequestId = instantNativeMobile
    ? `mobile-menu-help-${Date.now()}-${currentActivityGroup || 'menu'}`
    : null;
  const { helpAudio, helpString } = currentMenu;
  let helpWordAgsString = 0;
  let helpWordAgsAudio = '';
  let wordAudioSequence = [];
  let useCompositeWordHelp = false;
  let deferredWordlistRequest = null;

  const data = {};

  const getWordsFromVariable = (activeGroup, currentLang, otherLang) => {
    if (!activeGroup?.multilanguageWords?.length) return [];

    return activeGroup.multilanguageWords
      .map((item) => {
        if (!otherLang) {
          return item.locale === currentLang ? item.word : '';
        }

        return item?.otherLocales?.[currentLang]?.word || '';
      })
      .filter(Boolean);
  };

  const getLocalizedWordAudioSequence = (localizedWords) => localizedWords
    .map((wordData) => {
      const filename = String(wordData?.wordAudio || '').split('/').pop();
      const word = removeParenthesis(wordData?.word || '').trim();
      const firstLetter = word.charAt(0).toUpperCase();

      if (!filename || !firstLetter) return '';

      const localeFolder = getLocaleAudioFolderName(solutionLocale);
      return `${audioURL}/Generic/WordsDB/${localeFolder}/Words/${firstLetter}/${filename}`;
    })
    .filter(Boolean);

  if (currentActivityGroup && activityGroups) {
    const group = _.find(activityGroups, { _id: currentActivityGroup });

    if (group?.words?.length && !group.multilanguageWords?.length) {
      if (isMobileAppRuntime() && !isOnline()) {
        data.wordsInWordlist = group.words.map(removeParenthesis).join(', ');
      } else if (instantNativeMobile) {
        // Native V7 Help must react to the tap immediately. The authored word
        // names are already in memory, so use them for the first paint and do
        // the localization lookup after the modal is visible.
        data.wordsInWordlist = group.words.map(removeParenthesis).join(', ');
        deferredWordlistRequest = axios.post(
          '/api/flink-make/wordlists/get-words-by-names-and-locale',
          {
            words: group.words,
            locale: solutionLocale.code,
          }
        );
      } else {
        try {
          const result = await axios.post(
            '/api/flink-make/wordlists/get-words-by-names-and-locale',
            {
              words: group.words,
              locale: solutionLocale.code,
            }
          );

          const localizedWords = Array.isArray(result.data) ? result.data : [];
          data.wordsInWordlist = localizedWords
            .map((wordData) => removeParenthesis(wordData.word))
            .join(', ');
          wordAudioSequence = getLocalizedWordAudioSequence(localizedWords);
        } catch (error) {
          data.wordsInWordlist = group.words.map(removeParenthesis).join(', ');
        }
      }
    } else if (group?.multilanguageWords?.length) {
      const localizedHelp = group.multilangWordsHelp?.[solutionLocale.code] || {};
      helpWordAgsString = localizedHelp.helpString || 0;
      helpWordAgsAudio = localizedHelp.helpAudio || '';

      const otherLang = solutionLocale.code !== 'en';
      const localizedWords = getWordsFromVariable(
        group,
        solutionLocale.code,
        otherLang
      );

      data.wordsInWordlist = localizedWords.join(', ');

      wordAudioSequence = group.multilanguageWords
        .map((item, index) => {
          const localizedWord = otherLang
            ? item?.otherLocales?.[solutionLocale.code]
            : item;
          const filename = String(localizedWord?.wordAudio || '').split('/').pop();

          if (!filename) return '';

          const localeFolder = otherLang ? `/${solutionLocale.code}` : '';
          return `${s3bucketPublicURL}/WordAGs/${group._id}/${index}/en/Audio${localeFolder}/${filename}`;
        })
        .filter(Boolean);

      useCompositeWordHelp = otherLang && wordAudioSequence.length > 0;
    }
  }

  const string = findStringByNumber(strings, helpString);
  const resolvedAudio = resolveStringAudio(string, solutionLocale.code);
  const resolvedLocale = _.find(locales, { code: resolvedAudio.localeCode })
    || (resolvedAudio.localeCode === solutionLocale.code ? solutionLocale : null)
    || { code: 'en', name: 'English' };
  const stringAudio = resolvedAudio.filename;
  const localeFolder = getLocaleAudioFolderName(solutionLocale);
  const audioHelp = stringAudio
    ? buildStringAudioUrl(stringAudio, resolvedLocale)
    : helpAudio
      ? `${audioURL}/Generic/MenuHelp/${localeFolder}/${helpAudio}`
      : '';

  const audioWordAgsHelp = helpWordAgsAudio
    ? `${s3bucketPublicURL}/WordAGs/${currentActivityGroup}/HelpAudio/${solutionLocale.code}/${helpWordAgsAudio}`
    : '';

  const makeHelpPayload = (payloadData = data) => ({
    data: payloadData,
    stringNumber: helpWordAgsString || helpString,
    audioHelp: audioWordAgsHelp || audioHelp,
    helpAudioFilename: helpWordAgsAudio || stringAudio || helpAudio || '',
    wordAudioSequence,
    useCompositeWordHelp,
    localeCode: solutionLocale.code,
    activityGroupId: currentActivityGroup,
    compactMobile,
    menuHelp: true,
    ...(typeof customTranslate === 'function' ? { customTranslate } : {}),
    helpRequestId,
    audioDebugContext: `Menu Help | ${currentMenu.menuName || currentMenu.name || currentMenu._id || 'Unnamed menu'} | String #${helpWordAgsString || helpString || 'not set'} | Locale ${solutionLocale.code}`,
  });

  window.audioPlayer?.preloadMany?.([
    audioWordAgsHelp || audioHelp,
    ...wordAudioSequence,
  ]);

  dispatch(showHelp(makeHelpPayload()));

  if (deferredWordlistRequest) {
    deferredWordlistRequest
      .then((result) => {
        const activeHelp = getState().flinkPlay?.help;
        if (!activeHelp || activeHelp.helpRequestId !== helpRequestId) return;

        const localizedWords = Array.isArray(result.data) ? result.data : [];
        const localizedText = localizedWords
          .map((wordData) => removeParenthesis(wordData.word))
          .filter(Boolean)
          .join(', ');
        const localizedWordAudioSequence = getLocalizedWordAudioSequence(localizedWords);

        wordAudioSequence = localizedWordAudioSequence;
        window.audioPlayer?.preloadMany?.(localizedWordAudioSequence);

        if (localizedText) {
          dispatch(showHelp(makeHelpPayload({
            ...data,
            wordsInWordlist: localizedText,
          })));
        }
      })
      .catch(() => {
        // The already-open mobile Help keeps the authored fallback words. The
        // lookup is enrichment only and must never delay or close the dialog.
      });
  }
};

export const resetFlinkPlay = () => ({
  filter,
  type: RESET_FLINK_PLAY,
});

export const showVideo = (payload) => ({
  filter,
  type: SHOW_VIDEO,
  payload,
});

/*export const showHelpPromise = (payload) => (dispatch) => {
  return new Promise((resolve) => {
    dispatch({
      filter,
      type: SHOW_HELP,
      payload: { ...payload, cb: resolve },
    });
  });
};*/

export const showHelpPromise = (payload) => (dispatch) => {
  return new Promise((resolve) => {
    dispatch(showHelp({ ...payload, cb: resolve }));
  });
};

export const familyUpdate = (family) => ({
  filter,
  type: FAMILY_UPDATE,
  payload: family,
});

export const setMenuButtons = (payload) => ({
  filter,
  type: SET_MENU_BUTTONS,
  payload,
});

export const showFamilyReports = (payload) => ({
  filter,
  type: SHOW_FAMILY_REPORTS,
  payload,
});

export const showParentAdvice = (payload) => ({
  filter,
  type: SHOW_PARENT_ADVICE,
  payload,
});

export const showQuickstartVideos = (payload) => ({
  filter,
  type: SHOW_QUICKSTART_VIDEOS,
  payload,
});

export const setPrevMenuButtons = () => ({
  filter,
  type: SET_PREV_MENU_BUTTONS,
});

export const setMainMenuScroll = (scrollPosition) => ({
  filter,
  type: SAVE_MAIN_MENU_SCROLL_POSITION,
  payload: scrollPosition,
});

//export const saveSettingsAndCloseDialog = (learnerId, settings) => async (
export const saveSettingsAndCloseDialog = (learnerId, settings, onlyTeam = false) => async (
  dispatch,
  getState
) => {
  const {
    flinkPlay: { team },
  } = getState();

  //await dispatch(updateLearner(learnerId, { settings }));
  if (!onlyTeam)
    await dispatch(updateLearner(learnerId, { settings }));


  if (team && team.length) {
    const serverTeam = team.filter((learner) => !isMobileLocalFriend(learner));
    const localTeam = team.filter(isMobileLocalFriend).map((learner) => (
      updateMobileFamilyFriend(learner._id, {
        settings: learner.settings || settings,
      }) || { ...learner, settings: learner.settings || settings }
    ));

    if (serverTeam.length) {
      await updateTeam(
        serverTeam.map((learner) => ({
        learnerId: learner._id,
        data: {
          settings: {
            //...learner.settings,
            ...learner.settings ? learner.settings : settings,
            // selectedSolutionId: settings.selectedSolutionId,
          },
        },
        }))
      )(dispatch);
    }

    if (localTeam.length) {
      const refreshedServerTeam = (getState().flinkPlay.team || [])
        .filter((learner) => !isMobileLocalFriend(learner));
      dispatch({
        filter,
        type: UPDATE_TEAM,
        payload: [...refreshedServerTeam, ...localTeam],
      });
    }
  }
  dispatch(closeSettings());
};

export const updateLearner = (learnerId, data, options = {}) => async (
  dispatch,
  getState
) => {
  const {
    flinkPlay: { learner, learningCenter },
  } = getState();

  if (
    learningCenter &&
    learningCenter.config &&
    learningCenter.config.noReports
  ) {
    dispatch({
      filter,
      type: UPDATE_LEARNER,
      payload: { ...learner, ...data },
    });

    return { success: true };
  }
  
  if (isMobileAppRuntime() && !isOnline()) {
    const localLearner = mergeLearnerPatch(learner, learnerId, data) || { ...learner, ...data };

    dispatch({
      filter,
      type: UPDATE_LEARNER,
      payload: localLearner,
    });

    await persistOfflineLearnerUpdates([{ learnerId, data }]);
    await enqueueRequest({
      kind: 'learner-update',
      method: 'post',
      url: '/api/flink-play/update-learners',
      data: [{ learnerId, data }],
    });

    return { success: true, offline: true, queued: true };
  }

  console.log('updateLearner: ',data)

  const saveLearner = () =>
    axios
      .post('/api/flink-play/update-learners', [
        {
          learnerId,
          data,
        },
      ])
      .then((response) => {
        if (response.data.success) {
          dispatch({
            filter,
            type: UPDATE_LEARNER,
            payload: response.data.data[0],
          });

          return { success: true };
        }
      })
      .catch((err) => {
        console.log('updateLearner error: ',[learnerId,data]);
        alert('Error while saving family member data');
        return { success: false };
      });

  return options.silent === true
    ? saveLearner()
    : withLoading(dispatch, saveLearner);
};

export const getPreviousWeekTime = (familyId) => (dispatch) =>
  withLoading(dispatch, () =>
    axios
      .get(`/api/reports/previous-week-time/${familyId}`)
      .then((response) => {
        if (response.data.success) {
          dispatch({
            filter,
            type: SET_PREVIOUS_WEEK_TIME,
            payload: response.data.time,
          });
          return { success: true }
        }
      })
      .catch((err) => {
        console.log(err);
        return { success: false };
      })
  );

export const trackTime = (addTimeInApp) => (dispatch) =>
  withLoading(dispatch, () =>
    axios
      .post('/api/flink-play/track-time', { addTimeInApp })
      .then((response) => {
        if (response.data.success) {
          const { points } = response.data;
          if (points !== undefined) {
            dispatch(setFamilyPoints(points));
          }

          return { success: true };
        }
      })
      .catch((err) => {
        console.log(err);
        alert('Error while tracking time');
        return { success: false };
      })
  );

export const updateTeam = (array) => async (dispatch, getState) => {
  if (isMobileAppRuntime() && !isOnline()) {
    const updates = Array.isArray(array) ? array : [];
    const byId = new Map(
      updates
        .map((item) => [String(item?.learnerId || ''), item?.data || {}])
        .filter(([learnerId]) => learnerId)
    );
    const currentTeam = getState()?.flinkPlay?.team || [];
    const nextTeam = currentTeam.map((learner) => {
      const learnerId = String(learner?._id || learner?.id || '');
      const data = learnerId ? byId.get(learnerId) : null;
      return data ? { ...learner, ...data } : learner;
    });

    dispatch({
      filter,
      type: UPDATE_TEAM,
      payload: nextTeam,
    });

    await persistOfflineLearnerUpdates(updates);
    if (updates.length) {
      await enqueueRequest({
        kind: 'team-update',
        method: 'post',
        url: '/api/flink-play/update-learners',
        data: updates,
      });
    }

    return { success: true, offline: true, queued: updates.length > 0 };
  }

  return withLoading(dispatch, () =>
    axios
      .post('/api/flink-play/update-learners', array)
      .then((response) => {
        if (response.data.success) {
          const { data } = response.data;
          dispatch({
            filter,
            type: UPDATE_TEAM,
            payload: data,
          });

          return { success: true };
        }
      })
      .catch((err) => {
        console.log(err);
        alert('Error while saving family member data');
        return { success: false };
      })
  );
};

export const getThemeImages = (themeId) => (dispatch) =>
  withLoading(dispatch, () =>
    axios
      .get(`/api/flink-play/get-theme-images/${themeId}`)
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        console.log('Error while getting theme images', err);
      })
  );

export const setTemplateData = (payload) => ({
  filter,
  type: SET_TEMPLATE_DATA,
  payload,
});

export const getConcentrationAssetsList = () => (dispatch, getState) => {
  const {
    flinkPlay: { currentSettings },
  } = getState();

  const themeId = (currentSettings && currentSettings.theme) || '';

  return withLoading(dispatch, () =>
    axios
      .get(`/api/flink-play/get-concentration-assets-list/${themeId}`)
      .then((response) => {
        const data = { ...response.data };

        data.cardbacks = _.chain(data.cardbacks || [])
          .map((f) => `${filesURL}/${f}`)
          .shuffle()
          .value();
        data.rewardGraphics = _.chain(data.rewardGraphics || [])
          .map((f) => `${filesURL}/${f}`)
          .shuffle()
          .value();

        data.numberOfPlays = 0;

        dispatch(setTemplateData({ [CONCENTRATION]: data }));
        return data;
      })
      .catch((err) => {
        console.error('Error while getting theme images', err);
        return {};
      })
  );
};

export const setActivityGroup = (group) => ({
  filter,
  type: SET_ACTIVITY_GROUP,
  payload: group,
});

export const setActivityToPlay = (activity) => ({
  filter,
  type: SET_ACTIVITY_TO_PLAY,
  payload: activity,
});

const logMobileOfflineMenuFailure = (reason, details = {}) => {
  if (!isMobileAppRuntime() || isOnline()) return;

  const storage = (
    window.Capacitor?.Plugins?.FLCOfflineStorage
    || window.Capacitor?.Plugins?.Filesystem
    || null
  );
  if (!storage?.appendText) return;

  const record = {
    time: new Date().toISOString(),
    type: 'offline-menu-navigation-failure',
    reason,
    pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    ...details,
  };

  Promise.resolve(storage.appendText({
    path: 'flc-offline-v1/diagnostics/missing-assets.jsonl',
    text: `${JSON.stringify(record)}\n`,
  })).catch((error) => {
    console.warn('[mobile-offline][menu-navigation-log-failed]', error?.message || error);
  });
};

export const openMenuItem = (itemId, isItemGroup) => (dispatch, getState) => {
  const {
    flinkPlay: {
      solutionMenu: { activityGroups, activities },
    },
  } = getState();
  const mobileOffline = isMobileAppRuntime() && !isOnline();

  const fail = (reason, details, desktopMessage) => {
    if (mobileOffline) {
      logMobileOfflineMenuFailure(reason, {
        requestedItemId: itemId,
        requestedIsGroup: !!isItemGroup,
        activityGroupsCount: Array.isArray(activityGroups) ? activityGroups.length : 0,
        activitiesCount: Array.isArray(activities) ? activities.length : 0,
        ...details,
      });
      return null;
    }

    alert(desktopMessage);
    return null;
  };

  const getItem = (id, isGroup) => {
    if (isGroup) {
      const activityGroup = _.find(activityGroups, { _id: id });

      if (!activityGroup) {
        return fail(
          'activity-group-not-found',
          { lookupId: id },
          "Unexpected Error - Activity Group doesn't exists"
        );
      }

      if (!activityGroup.elements || !activityGroup.elements.length) {
        return fail(
          'activity-group-empty',
          { lookupId: id, activityGroup },
          'Activity Group is empty'
        );
      }

      if (activityGroup.elements.length === 1) {
        const elem = activityGroup.elements[0];
        const resolved = getItem(elem._id, elem.isGroup);

        // Mobile offline must never fall through to the native WebView alert.
        // If the cached menu contains a dangling single-child reference, keep
        // the parent group navigable and record the exact broken reference.
        if (!resolved && mobileOffline) {
          logMobileOfflineMenuFailure('single-child-resolution-failed', {
            lookupId: id,
            childId: elem && elem._id,
            childIsGroup: !!(elem && elem.isGroup),
            activityGroup,
          });
          return { _id: activityGroup._id, isGroup: true };
        }

        return resolved;
      } else {
        return { _id: activityGroup._id, isGroup: true };
      }
    } else {
      const activity = _.find(activities, { _id: id });

      if (!activity) {
        return fail(
          'activity-not-found',
          { lookupId: id },
          "Unexpected Error - Activity doesn't exists"
        );
      }

      return { _id: id, templateId: activity.templateId, isGroup: false };
    }
  };

  const itemToSet = getItem(itemId, isItemGroup);

  // console.info('-> MENU: Item to set', itemToSet);

  if (!itemToSet) {
    return fail('no-item-to-set', {}, 'No Item To Set');
  }

  // @TODO: Check if activity exists

  if (itemToSet.isGroup) {
    dispatch(setActivityGroup(itemToSet._id));
  } else {
    dispatch(setActivityToPlay(itemToSet._id));
  }
};

export const goBack = () => ({
  filter,
  type: GO_BACK_ACTION,
});

export const setParentAdviceCoords = (e, draggableEvent) => {
  return {
    filter,
    type: SET_PARENT_ADVICE_COORDS,
    payload: {
      x: draggableEvent.x,
      y: draggableEvent.y,
    },
  };
};

export const setFeedbackCoords = (e, draggableEvent) => {
  return {
    filter,
    type: SET_FEEDBACK_COORDS,
    payload: {
      x: draggableEvent.x,
      y: draggableEvent.y,
    },
  };
};
